import { isFromM, normalizeTrimmedString } from '@m2h/common/utils';

export const GOOGLE_MAP_CHANNEL = 'googlemap';

// 仅这些 URL channel 需要跨页面恢复；其他 URL channel 仍可在当前页面使用，但不持久化。这样这样做是为了不影响已有逻辑。
export const PERSISTABLE_URL_CHANNELS: readonly string[] = [GOOGLE_MAP_CHANNEL];

const CHANNEL_SESSION_KEY = 'm2h_channel';

const getStoredChannel = (): string => {
  try {
    const storedChannel = normalizeTrimmedString(sessionStorage.getItem(CHANNEL_SESSION_KEY));
    return PERSISTABLE_URL_CHANNELS.includes(storedChannel) ? storedChannel : '';
  } catch (error) {
    // sessionStorage 不可用时只降级到 Mach 环境，不能阻断页面初始化或请求。
    console.warn('Failed to read channel from sessionStorage:', error);
    return '';
  }
};

const storeChannel = (channel: string): void => {
  try {
    sessionStorage.setItem(CHANNEL_SESSION_KEY, channel);
  } catch (error) {
    // 持久化失败不影响当前 Runtime，channel 仍会同步到 Mach.env。
    console.warn('Failed to store channel in sessionStorage:', error);
  }
};

const removeStoredChannel = (): void => {
  try {
    sessionStorage.removeItem(CHANNEL_SESSION_KEY);
  } catch (error) {
    console.warn('Failed to clear channel from sessionStorage:', error);
  }
};

const getMachEnvChannel = (): string => normalizeTrimmedString(Mach?.env?.schemeParams?.channel);

const getMachDataChannel = (): string => normalizeTrimmedString(Mach?.data?.channel);

const syncToMachEnv = (channel: string): void => {
  if (!channel) return;
  Mach.env = Mach.env || {};
  Mach.env.schemeParams = Mach.env.schemeParams || {};
  // 兼容现有页面直接读取 Mach.env.schemeParams.channel 的方式。
  Mach.env.schemeParams.channel = channel;
};

const clearMachEnvChannel = (): void => {
  if (Mach?.env?.schemeParams) {
    delete Mach.env.schemeParams.channel;
  }
};

/**
 * 页面初始化时恢复 channel。
 * URL 是本次最新入口来源；URL 无 channel 时才恢复当前 Tab 中的持久化值。
 */
export const initChannel = (urlChannel?: string): string => {
  const normalizedUrlChannel = normalizeTrimmedString(urlChannel);

  if (normalizedUrlChannel) {
    // 所有 URL channel 都保持当前页优先，仅明确需要跨页恢复的渠道写入当前 Tab。
    if (PERSISTABLE_URL_CHANNELS.includes(normalizedUrlChannel)) {
      storeChannel(normalizedUrlChannel);
    } else {
      // 新 URL 已明确切换渠道，清除旧持久化值，避免后续页面错误恢复为 googlemap。
      removeStoredChannel();
    }
    syncToMachEnv(normalizedUrlChannel);
    return normalizedUrlChannel;
  }

  // URL 无 channel 时不能清空缓存；支付、登录或整页跳转返回通常不再携带入口参数。
  const storedChannel = getStoredChannel();
  const channel = storedChannel || getMachEnvChannel();
  if (channel) syncToMachEnv(channel);
  return channel;
};

/**
 * 页面缓存恢复可能整体替换 schemeParams；仅在当前页面没有 channel 时从当前 Tab 恢复。
 * 当前页面显式传入的 channel 优先，不能被历史 googlemap 来源覆盖。
 */
export const restoreChannelIfMissing = (): string => {
  const currentChannel = getMachEnvChannel();
  if (currentChannel) return currentChannel;

  const storedChannel = getStoredChannel();
  if (storedChannel) syncToMachEnv(storedChannel);
  return storedChannel;
};

/**
 * 页面业务统一读取入口。初始化完成后优先读取 Mach.env，并用 sessionStorage 兜底。
 */
export const getChannel = (): string => getMachDataChannel() || getMachEnvChannel() || getStoredChannel();

/**
 * 请求 Header 优先级：单接口原有 channel > 页面声明 channel > iframe/M 端 channel > 。
 */
export const getRequestChannel = (otherChannel?: string): string => {
  // sessionStorage 用于兜底页面首个请求早于初始化恢复的极端时序。
  const pageChannel = getChannel();
  const iframeChannel = isFromM() ? 'bd' : '';
  return normalizeTrimmedString(otherChannel) || pageChannel || iframeChannel;
};

export const isGoogleMapChannel = (channel?: string): boolean => normalizeTrimmedString(channel) === GOOGLE_MAP_CHANNEL;

export const clearChannel = (): void => {
  removeStoredChannel();
  clearMachEnvChannel();
};
